import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { UploadCloud, FileText, FolderUp } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { ApiError } from "@/api/client";
import {
  knowledgeBaseApi,
  type KbCategory,
  type KbTag,
  type KbBulkUploadRow,
} from "@/api/knowledgeBase";
import { useToast } from "@/components/ui/Toast";
import {
  KbDocumentMetadataForm,
  EMPTY_METADATA,
  type KbDocumentMetadataState,
} from "./KbDocumentMetadataForm";

// Knowledge Base v1.0 PR 5 — upload dialog. Single-file mode applies the
// per-file metadata template; multi-file (and folder) mode posts the whole
// batch to the new /documents/bulk endpoint which infers PrimaryCategory
// from each file's top-level folder name and triggers exactly ONE indexer
// run after the batch (never one per file — see the operator runbook).

interface KbDocumentUploadDialogProps {
  categories: KbCategory[];
  tags: KbTag[];
  onClose: () => void;
  onSaved: () => void;
}

// File.webkitRelativePath is non-standard but supported in every browser
// we target; TypeScript's lib.dom.d.ts already exposes it as `string`.
function relativePathFor(file: File): string {
  return file.webkitRelativePath || file.name;
}

export function KbDocumentUploadDialog({
  categories,
  tags,
  onClose,
  onSaved,
}: KbDocumentUploadDialogProps) {
  const toast = useToast();
  const [files, setFiles] = useState<File[]>([]);
  const [metadata, setMetadata] =
    useState<KbDocumentMetadataState>(EMPTY_METADATA);
  const [errors, setErrors] = useState<string[]>([]);
  const [bulkRows, setBulkRows] = useState<KbBulkUploadRow[] | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const isBulk = files.length > 1;

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!isBulk) {
        await knowledgeBaseApi.uploadDocument(files[0], buildPayload(metadata, files[0]));
        return { mode: "single" as const };
      }
      const result = await knowledgeBaseApi.bulkUploadDocuments(
        files.map((file) => ({ file, relativePath: relativePathFor(file) })),
      );
      return { mode: "bulk" as const, result };
    },
    onSuccess: (outcome) => {
      if (outcome.mode === "single") {
        toast.success("Document uploaded.");
        onSaved();
        return;
      }
      const { result } = outcome;
      const needsAttention =
        result.failedCount + result.uncategorizedNeedsReviewCount;
      if (needsAttention === 0) {
        toast.success(
          `${result.createdCount} uploaded, ${result.skippedDuplicateCount} duplicate skipped.`,
        );
        onSaved();
      } else {
        toast.error(
          `${result.createdCount} created · ${result.skippedDuplicateCount} duplicate · ` +
            `${result.uncategorizedNeedsReviewCount} need review · ${result.failedCount} failed.`,
        );
        setBulkRows(result.rows);
      }
    },
    onError: (err) => {
      if (err instanceof ApiError && err.errors.length) {
        setErrors(err.errors);
      } else {
        setErrors([err instanceof Error ? err.message : "Upload failed."]);
      }
    },
  });

  const validate = (): string[] => {
    const issues: string[] = [];
    if (files.length === 0) issues.push("Select at least one file to upload.");
    if (!isBulk) {
      if (!metadata.title.trim()) issues.push("Title is required.");
      if (!metadata.primaryCategoryCode)
        issues.push("Primary category is required.");
    }
    return issues;
  };

  const submit = () => {
    setErrors([]);
    setBulkRows(null);
    const issues = validate();
    if (issues.length) {
      setErrors(issues);
      return;
    }
    uploadMutation.mutate();
  };

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const next = Array.from(list);
    setFiles(next);
    setBulkRows(null);
    // Single-file mode: pre-fill the title from the file name (strip extension).
    if (next.length === 1 && !metadata.title.trim()) {
      const name = next[0].name.replace(/\.[^.]+$/, "");
      setMetadata({ ...metadata, title: name });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={
        files.length > 1
          ? `Bulk upload · ${files.length} files`
          : "Upload document"
      }
      description={
        isBulk
          ? "Each file's category is inferred from its top-level folder. Files whose folder doesn't match a seeded category are surfaced for admin review."
          : "Files are added to the curated library and indexed in the background."
      }
      size="lg"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={uploadMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            loading={uploadMutation.isPending}
          >
            <UploadCloud size={16} aria-hidden /> Upload
          </Button>
        </>
      }
    >
      <div className="space-y-space-4">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={
            "border-2 border-dashed rounded-md p-space-4 text-center transition-colors " +
            (dragOver ? "border-gold bg-gold/5" : "border-border-light bg-white-warm")
          }
        >
          <UploadCloud size={28} className="mx-auto text-gold-shadow" aria-hidden />
          <p className="text-small text-onyx mt-space-2">
            Drag files here, or pick{" "}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="font-display font-semibold text-gold underline-offset-4 hover:underline"
            >
              files
            </button>
            {" / "}
            <button
              type="button"
              onClick={() => folderInputRef.current?.click()}
              className="font-display font-semibold text-gold underline-offset-4 hover:underline"
            >
              a folder
            </button>
            .
          </p>
          <p className="text-caption text-gray-mid mt-space-1">
            Bulk uploads infer the category from each file's top-level folder
            (use the category code or display name). Single-file uploads use
            the metadata form below.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="sr-only"
            aria-label="Choose files"
            onChange={(e) => handleFiles(e.target.files)}
          />
          {/* webkitdirectory is non-standard but universally supported by the
              browsers we target — gives us file.webkitRelativePath populated
              with the full picked-folder relative path. */}
          <input
            ref={folderInputRef}
            type="file"
            // @ts-expect-error — webkitdirectory is not in the TypeScript HTMLInputElement lib
            webkitdirectory=""
            directory=""
            multiple
            className="sr-only"
            aria-label="Choose folder"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>

        {files.length > 0 && (
          <ul className="border border-border-light rounded-md divide-y divide-border-light max-h-64 overflow-auto">
            {files.map((f, idx) => (
              <li
                key={`${relativePathFor(f)}-${idx}`}
                className="flex items-center gap-space-2 px-space-3 py-space-2 text-small"
              >
                <FileText size={14} className="text-gray-mid" aria-hidden />
                <span className="flex-1 truncate" title={relativePathFor(f)}>
                  {relativePathFor(f)}
                </span>
                <span className="text-caption text-gray-mid">
                  {formatBytes(f.size)}
                </span>
              </li>
            ))}
          </ul>
        )}

        {isBulk ? (
          <div className="rounded-md border border-border-light bg-white-warm p-space-3 text-small text-onyx">
            <div className="flex items-center gap-space-2 font-display font-semibold mb-space-1">
              <FolderUp size={14} aria-hidden /> Bulk mode
            </div>
            <p className="text-caption text-gray-mid">
              Metadata is inferred from each file's folder; per-file
              metadata can be edited after upload. The batch triggers a
              single re-index when it finishes.
            </p>
          </div>
        ) : (
          <KbDocumentMetadataForm
            value={metadata}
            onChange={setMetadata}
            categories={categories}
            tags={tags}
            errors={errors}
          />
        )}

        {bulkRows && bulkRows.length > 0 && (
          <div className="rounded-md border border-border-light divide-y divide-border-light max-h-64 overflow-auto">
            {bulkRows.map((r, idx) => (
              <div
                key={`${r.fileName}-${idx}`}
                className="flex items-start gap-space-2 px-space-3 py-space-2 text-small"
              >
                <span
                  className={
                    "inline-flex shrink-0 px-space-2 py-px rounded text-caption font-display font-semibold " +
                    outcomeClass(r.outcome)
                  }
                >
                  {outcomeLabel(r.outcome)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{r.relativePath ?? r.fileName}</div>
                  {r.reason && (
                    <div className="text-caption text-gray-mid">{r.reason}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {errors.length > 0 && !bulkRows && (
          <ul className="text-caption text-status-error space-y-px">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function buildPayload(
  metadata: KbDocumentMetadataState,
  file: File,
): Parameters<typeof knowledgeBaseApi.uploadDocument>[1] {
  return {
    title:
      metadata.title.trim() ||
      file.name.replace(/\.[^.]+$/, ""),
    description: metadata.description.trim() || null,
    primaryCategoryCode: metadata.primaryCategoryCode,
    registryAbbrev: metadata.registryAbbrev.trim() || null,
    documentVersion: metadata.documentVersion.trim() || null,
    effectiveDate: metadata.effectiveDate || null,
    confidentiality: metadata.confidentiality,
    tagCodes: metadata.tagCodes,
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function outcomeLabel(o: KbBulkUploadRow["outcome"]): string {
  switch (o) {
    case "Created":
      return "Created";
    case "SkippedDuplicate":
      return "Duplicate";
    case "UncategorizedNeedsReview":
      return "Needs review";
    case "Failed":
      return "Failed";
  }
}

function outcomeClass(o: KbBulkUploadRow["outcome"]): string {
  switch (o) {
    case "Created":
      return "bg-status-success/15 text-status-success";
    case "SkippedDuplicate":
      return "bg-gold/15 text-gold-shadow";
    case "UncategorizedNeedsReview":
      return "bg-status-warning/15 text-status-warning";
    case "Failed":
      return "bg-status-error/15 text-status-error";
  }
}
